<template>
    <div class="divisionArea" style="margin-bottom: 16px">
        <div class="d-flex align-items-center input-group-scv">
            <span class="itemName">表示部署</span>
            <input type="text" class="input-text-scv flex-grow-1" v-model="divisionName" disabled>
            <input type="text" class="input-text-scv divisionAreaText" :value="isInheritDivision ? '配下を含む' : '配下を含まない'"
                disabled>

            <template v-if="enableDivisionChangeButton">
                <div style="padding: 0px 8px">
                    <button type="button" class="btn-scv-lightblue-small divisionSelectButton"
                        @click="showDialog('modalSelectDivision')">変更</button>
                </div>
            </template>
        </div>
    </div>

    <!-- 部署変更ダイアログ -->
    <Teleport to="body">
        <SelectDivisionDialog id="modalSelectDivision" title="部署変更" :selectedDivisionID="selectedDivisionId"
            :alertDivisionDisplay="alertDivisionDisplay" :enableInheritDivision="enableInheritDivision"
            :isInheritDivision="isInheritDivision" @onDivisionSelectChange="onDivisionSelectChange"
            ref="refSelectDivisionDialog" />
    </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { BootstrapUtil } from 'lib-vue-v2/util';
import SelectDivisionDialog from '@/components/SelectDivisionDialog.vue';
import { DivisionData } from "@/models/DivisionData";

// Propsの定義
const props = defineProps({
    enableDivisionChangeButton: { type: Boolean, default: false },
    selectedDivisionId: { type: Number, required: true },
    alertDivisionDisplay: { type: Boolean, default: false },     // 部署ツリーにアラート端末数を表示するかどうか
    enableInheritDivision: { type: Boolean, default: false },    // 配下部署を含むかどうかの選択ができるかどうか
    isInheritDivision: { type: Boolean, default: true },         // 配下部署含むかどうか
});

// Emitsの定義
const emit = defineEmits<{
    (e: 'onDivisionSelectChange', divisionId: number, divisionName: string, isInheritDivision: boolean): void
}>();

// リアクティブ変数の定義
const divisionName = ref("");
const refSelectDivisionDialog = ref<InstanceType<typeof SelectDivisionDialog> | null>(null);

/**
 * 部署情報を取得
 */
const getDivision = async (): Promise<void> => {
    // 表示部署変更ダイアログの部署情報を更新する。
    if (refSelectDivisionDialog.value) {
        // 一時的に any にキャストしてメソッドを呼び出す
        await (refSelectDivisionDialog.value as any).sendReqGetDivision();
    }
};

/**
 * 部署変更イベント
 * @param selectDivision 部署情報
 * @param isInherit 配下部署を含むかどうか（未定義を許容）
 */
const onDivisionSelectChange = (selectDivision: DivisionData, isInherit?: boolean): void => {
    divisionName.value = selectDivision.divisionUINamePath;

    // undefinedだった場合は、Propsのデフォルト値（または任意の値）を代替にする
    const actualIsInherit = isInherit ?? props.isInheritDivision;

    // 親に通知する
    emit("onDivisionSelectChange", selectDivision.divisionId, selectDivision.divisionName, actualIsInherit);
};

/**
 * 選択中の部署名(UI用フルパス)を取得します
 */
const getSelectedDivisionUINamePath = (): string => {
    return divisionName.value;
};

/**
 * ダイアログを表示します。
 * @param id 表示対象のid属性
 */
const showDialog = (id: string): void => {
    BootstrapUtil.showModal(id);
};

// 親コンポーネントからメソッドを呼び出せるように公開する
defineExpose({
    getDivision,
    getSelectedDivisionUINamePath
});
</script>

<style scoped>
.divisionArea {
    width: 100%;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0px 0px 2px 1px rgba(0, 0, 0, 0.25);
    padding: 16px;
    position: relative;
}

.itemName {
    width: 64px;
    padding-right: 8px;
    text-align: right;
    font-size: 14px;
    color: #242424;
    line-height: 1;
}

/* [変更]ボタン */
.divisionSelectButton {
    min-width: 80px;
}

/* 配下部署を含む/含まない 表示エリア */
.divisionAreaText {
    width: 140px;
    margin-left: 8px;
}
</style>
